import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Building2, MapPin, User, Phone, Mail, AlertCircle, Loader, ArrowRight } from 'lucide-react'
import toast from 'react-hot-toast'
import AuthHeader from '../../components/auth/AuthHeader'
import RegistrationSidebar from '../../components/registration/RegistrationSidebar'
import StepIndicator from '../../components/registration/StepIndicator'
import { vendorService } from '../../services/vendorService'
import { saveToLocalStorage, getFromLocalStorage, removeFromLocalStorage } from '../../utils/localStorage'
import { mapVendorRegisterToForm } from '../../utils/dataMapping'

const LegalEntityDetailsPage = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    legalEntityName: '',
    companyDisplayName: '',
    cin: '',
    gstin: '',
    pan: '',
    registeredAddress: {
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    operatingAddress: {
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    contactPerson: {
      name: '',
      role: '',
      email: '',
      mobile: '',
      countryCode: '+91'
    },
    preferredContactMethod: 'email'
  })

  const [errors, setErrors] = useState({})
  const [isSameAsRegistered, setIsSameAsRegistered] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isCheckingStatus, setIsCheckingStatus] = useState(true)
  const [userId, setUserId] = useState(null)
  const [countryCode, setCountryCode] = useState('+91')
  const [countryCodes, setCountryCodes] = useState([])
  const [countryCodesLoading, setCountryCodesLoading] = useState(true)
  const [isStepCompleted, setIsStepCompleted] = useState(false)

  // Load form data from localStorage on mount
  useEffect(() => {
    const savedData = getFromLocalStorage('vendor_registration_step1')
    if (savedData) {
      setFormData(prev => ({ ...prev, ...savedData.formData }))
      setIsSameAsRegistered(savedData.isSameAsRegistered || false)
      if (savedData.countryCode) {
        setCountryCode(savedData.countryCode)
      }
    }
  }, [])

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    if (formData.legalEntityName || formData.cin || formData.pan) {
      saveToLocalStorage('vendor_registration_step1', {
        formData,
        isSameAsRegistered,
        countryCode
      })
    }
  }, [formData, isSameAsRegistered, countryCode])

  // Check registration status on mount
  useEffect(() => {
    console.log('LegalEntityDetailsPage useEffect triggered')
    const checkRegistrationStatus = async () => {
      try {
        setIsCheckingStatus(true)
        console.log('LegalEntityDetailsPage - Starting checkRegistrationStatus')
        const user = await vendorService.getCurrentUser()
        console.log('LegalEntityDetailsPage - User data:', user)
        
        if (!user) {
          toast.error('Please log in to continue')
          navigate('/auth/login')
          return
        }

        const currentUserId = user.id || user.user_id
        setUserId(currentUserId)

        // Get vendorId early for use in API calls
        const vendorId = user.vendor_id || user.vendorId || sessionStorage.getItem('vendorId')

        // Check registration status
        const regStatus = user.hasCompletedRegistration
        const isCompleted = regStatus === true || 
                           regStatus === 'true' || 
                           regStatus === 1 || 
                           regStatus === '1' ||
                           String(regStatus).toLowerCase() === 'true'
        
        // Check admin review status - only allow dashboard if approved
        const adminReview = user.admin_review || user.adminReview
        if (isCompleted && adminReview === 'approved' && user.status === 'active') {
          navigate('/vendor/dashboard', { replace: true })
          return
        }
        
        // Navigation logic based on hasCompletedRegistration:
        // - true → Step 4 (Documents)
        // - "vendor-profile" → Step 3 (Subscription)
        // - "vendor-register" → Step 2 (Profile Building) - current step, allow editing
        // - false → Step 1 (Legal Details) - current step
        
        if (isCompleted) {
          // All steps completed - go to documents
          navigate('/vendor/registration/documents', { replace: true })
          return
        } else if (regStatus === 'vendor-profile' || regStatus === 'vendor_profile') {
          // Step 2 completed - should be on Step 3 (Subscription)
          navigate('/vendor/registration/subscription', { replace: true })
          return
        } else if (regStatus === 'vendor-register' || regStatus === 'vendor_register') {
          // User already completed step 1, allow them to edit or continue
          // Mark step as completed since they're on step 1 and it's already completed
          setIsStepCompleted(true)
          // Fetch existing data to populate form - force refresh to get latest data
          console.log('Step 1 - vendorId:', vendorId, 'regStatus:', regStatus)
          if (vendorId) {
            try {
              console.log('Calling getVendorRegisterData for vendorId:', vendorId, '(force refresh)')
              const response = await vendorService.getVendorRegisterData(vendorId, true) // Force refresh
              console.log('getVendorRegisterData response:', response)
              if (response?.success && response?.data) {
                const mappedData = mapVendorRegisterToForm(response.data)
                if (mappedData) {
                  // Merge with existing form data (API data takes precedence)
                  setFormData(prev => ({
                    ...prev,
                    ...mappedData,
                    // Keep companyDisplayName if not in API response
                    companyDisplayName: mappedData.companyDisplayName || prev.companyDisplayName
                  }))
                  // Set country code and same as registered flag
                  if (mappedData.contactPerson?.countryCode) {
                    setCountryCode(mappedData.contactPerson.countryCode)
                  }
                  if (mappedData.isSameAsRegistered !== undefined) {
                    setIsSameAsRegistered(mappedData.isSameAsRegistered)
                  }
                  // Show subtle notification if using cached data
                  if (response.cached) {
                    toast('Loaded saved data', { icon: 'ℹ️', duration: 2000 })
                  }
                }
              }
            } catch (error) {
              console.error('Error loading vendor register data:', error)
              // Non-blocking error - allow user to continue with empty form
              toast.error('Could not load saved data. You can continue with a new form.', { duration: 3000 })
            }
          }
        } else if (vendorId && !regStatus) {
          // No registration status set, but vendorId exists - try to load data
          console.log('Step 1 - No status but vendorId exists, loading data')
          try {
            console.log('Calling getVendorRegisterData (no status) for vendorId:', vendorId)
            const response = await vendorService.getVendorRegisterData(vendorId, true) // Force refresh
            console.log('getVendorRegisterData (no status) response:', response)
            if (response?.success && response?.data) {
              const mappedData = mapVendorRegisterToForm(response.data)
              if (mappedData) {
                // Only update if form is still empty (don't overwrite user input)
                setFormData(prev => {
                  const hasData = prev.legalEntityName || prev.cin || prev.pan
                  if (hasData) return prev // Don't overwrite if user already has data
                  
                  return {
                    ...prev,
                    ...mappedData,
                    companyDisplayName: mappedData.companyDisplayName || prev.companyDisplayName
                  }
                })
                if (mappedData.contactPerson?.countryCode) {
                  setCountryCode(mappedData.contactPerson.countryCode)
                }
                if (mappedData.isSameAsRegistered !== undefined) {
                  setIsSameAsRegistered(mappedData.isSameAsRegistered)
                }
              }
            }
          } catch (error) {
            console.error('Error loading vendor register data (no status):', error)
          }
        }
        
        // Redirect after data loading if needed
        if (regStatus === 'vendor-profile' || regStatus === 'vendor_profile') {
          navigate('/vendor/registration/profile', { replace: true })
          return
        }
      } catch (error) {
        console.error('LegalEntityDetailsPage - Error in checkRegistrationStatus:', error)
        toast.error('Error loading user information')
        navigate('/auth/login')
      } finally {
        setIsCheckingStatus(false)
      }
    }

    checkRegistrationStatus()
  }, [navigate])

  // Fetch country codes on component mount
  useEffect(() => {
    const fetchCountryCodes = async () => {
      try {
        setCountryCodesLoading(true)
        const response = await vendorService.getEnum('country_codes')
        if (response.success && response.data) {
          const codes = response.data.map(item => item.value)
          setCountryCodes(codes)
        } else {
          setCountryCodes(['+91'])
        }
      } catch (error) {
        console.error('Failed to fetch country codes:', error)
        setCountryCodes(['+91'])
      } finally {
        setCountryCodesLoading(false)
      }
    }

    fetchCountryCodes()
  }, [])

  // Validation functions
  const validateCIN = (cin) => {
    const trimmedCin = cin.trim().toUpperCase()
    const standardCinRegex = /^[LU]\d{5}[A-Z]{2}\d{4}[A-Z]{3}\d{6}$/
    const shortCinRegex = /^[A-Z]{2,4}-\d{3,6}$/
    const numericCinRegex = /^[A-Z0-9\-]{7,21}$/
    return standardCinRegex.test(trimmedCin) || shortCinRegex.test(trimmedCin) || numericCinRegex.test(trimmedCin)
  }

  const validateGSTIN = (gstin) => {
    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/
    return gstinRegex.test(gstin)
  }

  const validatePAN = (pan) => {
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/
    return panRegex.test(pan)
  }

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validateMobile = (mobile, countryCode = '+91') => {
    // Mobile input field only contains digits (no country code)
    const cleanMobile = mobile.trim()
    
    // For India (+91), validate 10 digits starting with 6-9
    if (countryCode === '+91') {
      // Must be exactly 10 digits and start with 6, 7, 8, or 9
      if (cleanMobile.length !== 10) {
        return false
      }
      // First digit must be 6, 7, 8, or 9 (valid Indian mobile prefixes)
      return /^[6-9]\d{9}$/.test(cleanMobile)
    }
    
    // For other countries, validate length (7-15 digits is standard)
    if (cleanMobile.length < 7 || cleanMobile.length > 15) {
      return false
    }
    
    // Must contain only digits
    return /^\d+$/.test(cleanMobile)
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.legalEntityName.trim()) {
      newErrors.legalEntityName = 'Legal entity name is required'
    }

    if (!formData.cin.trim()) {
      newErrors.cin = 'CIN/LLP-IN is required'
    } else if (!validateCIN(formData.cin)) {
      newErrors.cin = 'Invalid CIN/LLP-IN format'
    }

    if (formData.gstin && !validateGSTIN(formData.gstin)) {
      newErrors.gstin = 'Invalid GSTIN format (15 characters)'
    }

    if (!formData.pan.trim()) {
      newErrors.pan = 'Company PAN is required'
    } else if (!validatePAN(formData.pan)) {
      newErrors.pan = 'Invalid PAN format (10 characters)'
    }

    if (!formData.registeredAddress.addressLine1.trim()) {
      newErrors.registeredAddress = 'Registered address is required'
    }
    if (!formData.registeredAddress.city.trim()) {
      newErrors.registeredAddress = 'City is required'
    }
    if (!formData.registeredAddress.state.trim()) {
      newErrors.registeredAddress = 'State is required'
    }
    if (!formData.registeredAddress.pincode.trim()) {
      newErrors.registeredAddress = 'Pincode is required'
    }

    if (!isSameAsRegistered) {
      if (!formData.operatingAddress.addressLine1.trim()) {
        newErrors.operatingAddress = 'Operating address is required'
      }
      if (!formData.operatingAddress.city.trim()) {
        newErrors.operatingAddress = 'City is required'
      }
      if (!formData.operatingAddress.state.trim()) {
        newErrors.operatingAddress = 'State is required'
      }
      if (!formData.operatingAddress.pincode.trim()) {
        newErrors.operatingAddress = 'Pincode is required'
      }
    }

    if (!formData.contactPerson.name.trim()) {
      newErrors.contactPerson = 'Contact person name is required'
    }
    if (!formData.contactPerson.role.trim()) {
      newErrors.contactPerson = 'Contact person role is required'
    }
    if (!formData.contactPerson.email.trim()) {
      newErrors.contactPerson = 'Contact person email is required'
    } else if (!validateEmail(formData.contactPerson.email)) {
      newErrors.contactPerson = 'Invalid email format'
    }
    if (!formData.contactPerson.mobile.trim()) {
      newErrors.contactPerson = 'Contact person mobile is required'
    } else {
      const currentCountryCode = formData.contactPerson.countryCode || countryCode
      if (!validateMobile(formData.contactPerson.mobile, currentCountryCode)) {
        if (currentCountryCode === '+91') {
          newErrors.contactPerson = 'Mobile number must be 10 digits starting with 6, 7, 8, or 9'
        } else {
          newErrors.contactPerson = 'Mobile number must be 7-15 digits'
        }
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }))
    }
  }

  const handleSameAsRegistered = (checked) => {
    setIsSameAsRegistered(checked)
    if (checked) {
      // Copy registered address to operating address
      setFormData(prev => ({
        ...prev,
        operatingAddress: { ...prev.registeredAddress }
      }))
    }
    // When unchecked, keep the form visible with current values (don't clear)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!userId) {
      toast.error('User not authenticated. Please log in again.')
      navigate('/auth/login')
      return
    }

    if (!validateForm()) {
      toast.error('Please fix the errors before continuing.')
      return
    }

    try {
      setIsLoading(true)

      const dataToSave = {
        user_id: userId,
        legalEntityName: formData.legalEntityName,
        companyDisplayName: formData.companyDisplayName,
        cin: formData.cin,
        gstin: formData.gstin,
        pan: formData.pan,
        registeredAddress: {
          addressLine1: formData.registeredAddress.addressLine1,
          addressLine2: formData.registeredAddress.addressLine2,
          city: formData.registeredAddress.city,
          state: formData.registeredAddress.state,
          pincode: formData.registeredAddress.pincode,
          country: formData.registeredAddress.country
        },
        operatingAddress: {
          addressLine1: formData.operatingAddress.addressLine1,
          addressLine2: formData.operatingAddress.addressLine2,
          city: formData.operatingAddress.city,
          state: formData.operatingAddress.state,
          pincode: formData.operatingAddress.pincode,
          country: formData.operatingAddress.country
        },
        contactPerson: {
          name: formData.contactPerson.name,
          role: formData.contactPerson.role,
          email: formData.contactPerson.email,
          mobile: `${formData.contactPerson.countryCode || countryCode}${formData.contactPerson.mobile}`,
          countryCode: formData.contactPerson.countryCode || countryCode
        },
        preferredContactMethod: formData.preferredContactMethod
      }

      const result = await vendorService.saveLegalDetails(dataToSave)

      if (result?.vendor_id || result?.data?.vendor_id) {
        const vendorId = result.vendor_id || result.data.vendor_id
        sessionStorage.setItem('vendorId', String(vendorId))
        
        // Update user registration status
        const userDataString = sessionStorage.getItem('userData')
        if (userDataString) {
          const userData = JSON.parse(userDataString)
          userData.hasCompletedRegistration = 'vendor-register'
          sessionStorage.setItem('userData', JSON.stringify(userData))
        }
      }

      // Clear localStorage after successful submission
      removeFromLocalStorage('vendor_registration_step1')
      
      toast.success('Legal details saved successfully!')
      navigate('/vendor/registration/profile')
    } catch (error) {
      toast.error(error.message || 'Failed to save legal entity details. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  if (isCheckingStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-10 h-10 mx-auto mb-4 animate-spin text-primary-600" />
          <p className="text-secondary-600 dark:text-secondary-400">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900">
      <AuthHeader />

      <div className="flex flex-col items-center min-h-[calc(100vh-64px)] p-4 py-4 gap-4">
        {/* Step Indicator - Outside the white card */}
        <div className="w-full max-w-6xl">
          <StepIndicator currentStep={1} />
        </div>

        <div className="flex items-start justify-center w-full gap-4">
          {/* Sidebar on the left */}
          <RegistrationSidebar currentStep={1} isStepCompleted={isStepCompleted} />
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card w-full max-w-6xl p-5 md:p-6 shadow-glow flex-1"
          >
            {/* Header */}
          <div className="text-center mb-6 pb-6 border-b border-secondary-200 dark:border-secondary-700">
            <h1 className="text-3xl font-display font-bold text-secondary-900 dark:text-secondary-100 mb-2 tracking-tight">
              Company Details
            </h1>
            <p className="text-sm text-secondary-600 dark:text-secondary-400 max-w-md mx-auto leading-relaxed">
              Legal entity information and registration details
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Category 1: Company Information - Full Width */}
            <div className="space-y-3 p-3.5 rounded-lg bg-secondary-50/50 dark:bg-secondary-800/30 border border-secondary-200 dark:border-secondary-700">
              <h3 className="text-sm font-semibold text-secondary-900 dark:text-secondary-100 flex items-center gap-2 pb-1 border-b border-secondary-200 dark:border-secondary-700">
                <Building2 className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                Company Information
              </h3>
              
              <div className="space-y-2.5">
                {/* Legal Entity Name */}
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                    Legal Entity Name <span className="text-error-500">*</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-secondary-400 w-3.5 h-3.5 z-10" />
                    <input
                      type="text"
                      value={formData.legalEntityName}
                      onChange={(e) => handleChange('legalEntityName', e.target.value)}
                      className="input pl-9 h-9 text-sm"
                      placeholder="Enter legal entity name"
                      required
                    />
                  </div>
                  {errors.legalEntityName && (
                    <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                      <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                      {errors.legalEntityName}
                    </p>
                  )}
                </div>

                {/* Company Display Name */}
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                    Company Display Name
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-secondary-400 w-3.5 h-3.5 z-10" />
                    <input
                      type="text"
                      value={formData.companyDisplayName}
                      onChange={(e) => handleChange('companyDisplayName', e.target.value)}
                      className="input pl-9 h-9 text-sm"
                      placeholder="Enter display name"
                    />
                  </div>
                </div>

                {/* CIN, PAN, GSTIN */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  <div className="space-y-1">
                    <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                      CIN/LLP-IN <span className="text-error-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.cin}
                      onChange={(e) => handleChange('cin', e.target.value.toUpperCase())}
                      className="input h-9 text-sm"
                      placeholder="e.g., L12345AB1234ABC123456"
                      required
                    />
                    {errors.cin && (
                      <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                        <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                        {errors.cin}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                      PAN <span className="text-error-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.pan}
                      onChange={(e) => handleChange('pan', e.target.value.toUpperCase())}
                      className="input h-9 text-sm"
                      placeholder="ABCDE1234F"
                      required
                    />
                    {errors.pan && (
                      <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                        <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                        {errors.pan}
                      </p>
                    )}
                  </div>
                </div>

                {/* GSTIN */}
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                    GSTIN (if applicable)
                  </label>
                  <input
                    type="text"
                    value={formData.gstin}
                    onChange={(e) => handleChange('gstin', e.target.value.toUpperCase())}
                    className="input h-9 text-sm"
                    placeholder="Enter GSTIN"
                  />
                  {errors.gstin && (
                    <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                      <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                      {errors.gstin}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Address Cards - Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Category 2: Registered Address */}
              <div className="space-y-3 p-3.5 rounded-lg bg-secondary-50/50 dark:bg-secondary-800/30 border border-secondary-200 dark:border-secondary-700">
                  <h3 className="text-sm font-semibold text-secondary-900 dark:text-secondary-100 flex items-center gap-2 pb-1 border-b border-secondary-200 dark:border-secondary-700">
                    <MapPin className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    Registered Address
                  </h3>
                  
                  <div className="space-y-2.5">
                    <div className="space-y-1">
                      <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                        Address Line 1 <span className="text-error-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.registeredAddress.addressLine1}
                        onChange={(e) => handleChange('registeredAddress.addressLine1', e.target.value)}
                        className="input h-9 text-sm"
                        placeholder="Enter address line 1"
                        required
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                        Address Line 2
                      </label>
                      <input
                        type="text"
                        value={formData.registeredAddress.addressLine2}
                        onChange={(e) => handleChange('registeredAddress.addressLine2', e.target.value)}
                        className="input h-9 text-sm"
                        placeholder="Enter address line 2"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2.5">
                      <div className="space-y-1">
                        <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                          City <span className="text-error-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.registeredAddress.city}
                          onChange={(e) => handleChange('registeredAddress.city', e.target.value)}
                          className="input h-9 text-sm"
                          placeholder="Enter city"
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                          State <span className="text-error-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.registeredAddress.state}
                          onChange={(e) => handleChange('registeredAddress.state', e.target.value)}
                          className="input h-9 text-sm"
                          placeholder="Enter state"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2.5">
                      <div className="space-y-1">
                        <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                          Pincode <span className="text-error-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.registeredAddress.pincode}
                          onChange={(e) => handleChange('registeredAddress.pincode', e.target.value)}
                          className="input h-9 text-sm"
                          placeholder="Enter pincode"
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                          Country
                        </label>
                        <input
                          type="text"
                          value={formData.registeredAddress.country}
                          onChange={(e) => handleChange('registeredAddress.country', e.target.value)}
                          className="input h-9 text-sm"
                          placeholder="Enter country"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {errors.registeredAddress && (
                    <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                      <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                      {errors.registeredAddress}
                    </p>
                  )}
              </div>

              {/* Category 3: Operating Address */}
              <div className="space-y-3 p-3.5 rounded-lg bg-secondary-50/50 dark:bg-secondary-800/30 border border-secondary-200 dark:border-secondary-700">
                  <div className="flex items-center justify-between pb-1 border-b border-secondary-200 dark:border-secondary-700">
                    <h3 className="text-sm font-semibold text-secondary-900 dark:text-secondary-100 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                      Operating Address
                    </h3>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isSameAsRegistered}
                        onChange={(e) => handleSameAsRegistered(e.target.checked)}
                        className="w-3.5 h-3.5 border-2 border-secondary-300 dark:border-secondary-600 rounded focus:ring-2 focus:ring-primary-500"
                      />
                      <span className="text-xs text-secondary-600 dark:text-secondary-400">Same as registered</span>
                    </label>
                  </div>
                  
                  <div className="space-y-2.5">
                    {isSameAsRegistered && (
                      <div className="mb-2 p-2 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded text-xs text-primary-700 dark:text-primary-300">
                        ✓ Operating address filled from registered address. You can edit it below.
                      </div>
                    )}
                    <div className="space-y-1">
                      <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                        Address Line 1 <span className="text-error-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.operatingAddress.addressLine1}
                        onChange={(e) => handleChange('operatingAddress.addressLine1', e.target.value)}
                        className="input h-9 text-sm"
                        placeholder="Enter address line 1"
                        required={!isSameAsRegistered}
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                        Address Line 2
                      </label>
                      <input
                        type="text"
                        value={formData.operatingAddress.addressLine2}
                        onChange={(e) => handleChange('operatingAddress.addressLine2', e.target.value)}
                        className="input h-9 text-sm"
                        placeholder="Enter address line 2"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2.5">
                      <div className="space-y-1">
                        <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                          City <span className="text-error-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.operatingAddress.city}
                          onChange={(e) => handleChange('operatingAddress.city', e.target.value)}
                          className="input h-9 text-sm"
                          placeholder="Enter city"
                          required={!isSameAsRegistered}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                          State <span className="text-error-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.operatingAddress.state}
                          onChange={(e) => handleChange('operatingAddress.state', e.target.value)}
                          className="input h-9 text-sm"
                          placeholder="Enter state"
                          required={!isSameAsRegistered}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2.5">
                      <div className="space-y-1">
                        <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                          Pincode <span className="text-error-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.operatingAddress.pincode}
                          onChange={(e) => handleChange('operatingAddress.pincode', e.target.value)}
                          className="input h-9 text-sm"
                          placeholder="Enter pincode"
                          required={!isSameAsRegistered}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                          Country
                        </label>
                        <input
                          type="text"
                          value={formData.operatingAddress.country}
                          onChange={(e) => handleChange('operatingAddress.country', e.target.value)}
                          className="input h-9 text-sm"
                          placeholder="Enter country"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {errors.operatingAddress && (
                    <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                      <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                      {errors.operatingAddress}
                    </p>
                  )}
              </div>
            </div>

            {/* Category 4: Contact Information - Full Width */}
            <div className="space-y-3 p-3.5 rounded-lg bg-secondary-50/50 dark:bg-secondary-800/30 border border-secondary-200 dark:border-secondary-700">
                  <h3 className="text-sm font-semibold text-secondary-900 dark:text-secondary-100 flex items-center gap-2 pb-1 border-b border-secondary-200 dark:border-secondary-700">
                    <User className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    Contact Information
                  </h3>
                  
                  <div className="space-y-2.5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                      <div className="space-y-1">
                        <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                          Contact Person Name <span className="text-error-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.contactPerson.name}
                          onChange={(e) => handleChange('contactPerson.name', e.target.value)}
                          className="input h-9 text-sm"
                          placeholder="Enter name"
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                          Designation <span className="text-error-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.contactPerson.role}
                          onChange={(e) => handleChange('contactPerson.role', e.target.value)}
                          className="input h-9 text-sm"
                          placeholder="Enter designation"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                      <div className="space-y-1">
                        <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                          Email <span className="text-error-500">*</span>
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-secondary-400 w-3.5 h-3.5 z-10" />
                          <input
                            type="email"
                            value={formData.contactPerson.email}
                            onChange={(e) => handleChange('contactPerson.email', e.target.value)}
                            className="input pl-9 h-9 text-sm"
                            placeholder="Enter email"
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                          Mobile <span className="text-error-500">*</span>
                        </label>
                        <div className="flex gap-2">
                          <select
                            value={formData.contactPerson.countryCode || countryCode}
                            onChange={(e) => {
                              const code = e.target.value
                              setCountryCode(code)
                              handleChange('contactPerson.countryCode', code)
                            }}
                            disabled={countryCodesLoading}
                            className={`input w-12 h-9 text-xs appearance-none cursor-pointer ${
                              countryCodesLoading ? "bg-secondary-100 dark:bg-secondary-800/50 cursor-not-allowed opacity-70" : ""
                            }`}
                          >
                            {countryCodesLoading ? (
                              <option>+91</option>
                            ) : countryCodes.length === 0 ? (
                              <option value="+91">+91</option>
                            ) : (
                              countryCodes.map((code) => (
                                <option key={code} value={code}>
                                  {code}
                                </option>
                              ))
                            )}
                          </select>
                          <div className="relative flex-1">
                            <Phone className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-secondary-400 w-3.5 h-3.5 z-10" />
                            <input
                              type="tel"
                              value={formData.contactPerson.mobile}
                              onChange={(e) => {
                                let value = e.target.value.replace(/[^0-9]/g, '')
                                handleChange('contactPerson.mobile', value)
                              }}
                              className="input pl-9 h-9 text-sm"
                              placeholder="XXXXXXXXXX"
                              required
                              maxLength={15}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-1 pt-1">
                      <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300 flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5" />
                        Preferred Contact Method
                      </label>
                      <div className="flex gap-4 pt-1">
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="preferredContactMethod"
                            value="email"
                            checked={formData.preferredContactMethod === 'email'}
                            onChange={(e) => handleChange('preferredContactMethod', e.target.value)}
                            className="w-3.5 h-3.5 border-2 border-secondary-300 dark:border-secondary-600 focus:ring-2 focus:ring-primary-500"
                          />
                          <span className="text-xs text-secondary-600 dark:text-secondary-400">Email</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="preferredContactMethod"
                            value="phone"
                            checked={formData.preferredContactMethod === 'phone'}
                            onChange={(e) => handleChange('preferredContactMethod', e.target.value)}
                            className="w-3.5 h-3.5 border-2 border-secondary-300 dark:border-secondary-600 focus:ring-2 focus:ring-primary-500"
                          />
                          <span className="text-xs text-secondary-600 dark:text-secondary-400">Phone</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {errors.contactPerson && (
                    <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                      <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                      {errors.contactPerson}
                    </p>
                  )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end mt-4">
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary h-10 px-6 text-xs font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              >
              {isLoading ? (
                <>
                  <Loader className="w-3.5 h-3.5 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <span>Continue to Profile Setup</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
              </button>
            </div>
          </form>
        </motion.div>
        </div>
      </div>
    </div>
  )
}

export default LegalEntityDetailsPage
